import React from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useHookFormMask } from 'use-mask-input';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { CURRENCY_MAP, DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/pricingPlan/utils/constants';
import { PricingPlanTickRateFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { DAY_OF_WEEK } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type TickRateFormProps = {
    form: UseFormReturn<PricingPlanTickRateFormSchemaValues>;
    onSubmit: (values: PricingPlanTickRateFormSchemaValues) => void;
};

function TickRateForm({ onSubmit, form }: TickRateFormProps) {
    const { formatMessage } = useIntl();
    const [displayRate, setDisplayRate] = React.useState('');
    const [displayMaxAmount, setDisplayMaxAmount] = React.useState('');
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const { data: pricingPlan } = useGetPricingPlan({ planId, parkingId });
    const { default_currency } = React.useContext(SettingsContext);

    const currency = React.useMemo(
        () => (pricingPlan?.currency ? formatMessage(CURRENCY_MAP[pricingPlan.currency]) : default_currency),
        [default_currency, formatMessage, pricingPlan],
    );
    const registerWithMask = useHookFormMask(form.register);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<PricingPlanTickRateFormSchemaValues>
                form={form}
                name="start_day"
                label={formatMessage(pricingPlanMessages.startDay)}
                options={Object.values(DAY_OF_WEEK)}
                optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
            />
            <FormField
                control={form.control}
                name="from_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.fromHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('from_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<PricingPlanTickRateFormSchemaValues>
                form={form}
                name="end_day"
                label={formatMessage(pricingPlanMessages.endDay)}
                options={Object.values(DAY_OF_WEEK)}
                optionTranslationMap={DAY_OF_WEEK_TRANSLATIONS_MAP}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.toHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <div className="relative flex w-full min-w-32 items-center gap-2">
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        size="sm"
                                        className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="00-23:59"
                                        inputMode="numeric"
                                        className="pl-8 text-sm"
                                        {...registerWithMask('to_time', 'datetime', {
                                            inputFormat: 'HH:mm',
                                            outputFormat: 'HH:mm',
                                            showMaskOnHover: false,
                                            placeholder: '0',
                                        })}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="rate_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.currencyRate} values={{ currency }} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.currencyRate, { currency })}...`}
                                    value={
                                        displayRate !== ''
                                            ? displayRate
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayRate(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (displayRate === '' || isNaN(parseFloat(displayRate))) return;
                                        const formatted = parseFloat(displayRate).toFixed(2);
                                        setDisplayRate(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="max_amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPlanMessages.maxAmountCurrency} values={{ currency }} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.maxAmountCurrency, { currency })}...`}
                                    moreInfo={formatMessage(pricingPlanMessages.maxAmountMoreInfo)}
                                    value={
                                        displayMaxAmount !== ''
                                            ? displayMaxAmount
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayMaxAmount(inputValue);

                                        // An empty value removes the limit
                                        const numeric = parseFloat(inputValue);
                                        field.onChange(isNaN(numeric) ? null : Math.round(numeric * 100));
                                    }}
                                    onBlur={() => {
                                        if (displayMaxAmount === '' || isNaN(parseFloat(displayMaxAmount))) return;
                                        const formatted = parseFloat(displayMaxAmount).toFixed(2);
                                        setDisplayMaxAmount(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default TickRateForm;
